import Title_ru_RU       from "@/core/misc/decorators/Title_ru_RU";
import TYPES             from "@/core/services/Changelogs/types";
import Core              from '@/core/Core'
import Bank              from "@/domains/Banks/Bank";
import BaseEntity        from "@/core/misc/BaseEntity";
import Company           from "@/domains/Companies/Company";
import Partner           from "@/domains/Partners/Partner";
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";

@Title_ru_RU_Class('Расчётный счёт')                // bankAccount → BankAccount
class BankAccount extends BaseEntity {

  static Events = {
    Created: 'bank-account:created',
    Updated: 'bank-account:updated',
    Deleted: 'bank-account:deleted'
  } as const;

  /* р/с */
  @Title_ru_RU('р/с', TYPES.STRING)
  public account: string;
  @Title_ru_RU('валюта счёта', TYPES.STRING)
  public currencyCode: string;
  /* основной р/с */
  @Title_ru_RU('основной р/с', TYPES.BOOLEAN)
  public isMain: boolean;

  @Title_ru_RU('Банк', TYPES.OBJECT)
  public bank: Bank | null;

  /** вообще это поля Bank, по тех. причинам на беке оно на уровне BankAccount,
   * в будущем будет перенесено в Bank скорее всего */

  public swift: string | null;

  /* обратная ссылка на компанию */
  private _company: Company | null;

  static fromPartner(partner: Partner): BankAccount {
    const account = new this();

    account.currencyCode = partner.country.localCurrency.code;

    /** если компания одна, то назначаем её, если их больше, то ручной выбор */

    account.company = partner.companies.length === 1 ? partner.companies[0] : null;

    account.bank = new Bank();

    account.swift = null;

    return account;
  }

  static fromServer(data: any, result = new BankAccount()): BankAccount {
    super.fromServer(data, result);

    result.bank = Core.utils.hydrate(data.bank, Bank.fromServer);

    return result;
  }

  get company(): Company | null {
    return this._company;
  }

  set company(value: Company | null) {
    this._company = value;
  }

  /** Форматированный номер счёта с учётом страны банка */
  get formattedAccount(): string {
    switch (this.bank?.address.countryCode) {
      case 'RU':
        return Core.utils.regional.ru.formatBankAccount(this.account) ?? this.account;
      case 'UZ':
        return Core.utils.regional.uz.formatBankAccount(this.account) ?? this.account;
      default:
        return this.account;
    }
  }

  toString(): string {
    if (this.isExisting === false) {
      return 'Добавление расчётного счёта';
    }
    else if (this.bank?.name) {
      return `*${this.account.slice(-5)} в ${this.bank.name}`;
    }
    else {
      return this.formattedAccount;
    }
  }

  /** Причина, по которой счёт нельзя сделать основным. null — можно. */
  getReasonCannotSetMain(): string | null {
    if (this.isMain) {
      return 'Счёт уже является основным';
    } else {
      return null;
    }
  }

  // ============================================================
  // Доменная логика ввода: маски, лейблы и валидации по стране банка/компании
  // ============================================================

  /** Код страны для выбора масок/валидаций. Берётся из страны компании счёта. */
  get countryCode(): string | null {
    if (this.company) {
      return this.company.country?.code ?? null;
    } else {
      return null;
    }
  }

  /** Маска ввода номера счёта по стране. */
  getAccountMask(): string {
    switch (this.countryCode) {
      case 'RU':
        return Core.utils.regional.ru.getBankAccountMask();
      case 'UZ':
        return Core.utils.regional.uz.getBankAccountMask();
      default:
        return '';
    }
  }

  /** Маска ввода идентификатора банка (БИК/МФО) по стране. */
  getBankIdMask(): string {
    switch (this.countryCode) {
      case 'RU':
        return Core.utils.regional.ru.getBankIdMask();
      case 'UZ':
        return Core.utils.regional.uz.getBankIdMask();
      default:
        return '';
    }
  }

  /** Подпись поля идентификатора банка по стране (БИК / МФО). */
  getBankIdLabel(): string {
    switch (this.countryCode) {
      case 'RU':
        return 'БИК банка';
      case 'UZ':
        return 'МФО банка';
      default:
        return 'БИК банка';
    }
  }

  /** Длина идентификатора банка, при которой нужно подгружать реквизиты банка. */
  getBankIdCompleteLength(): number | null {
    switch (this.countryCode) {
      case 'RU':
        return 9;
      case 'UZ':
        return 5;
      default:
        return null;
    }
  }

  /** Валидация номера счёта по стране. */
  validateAccountNumber(): string | null {
    switch (this.countryCode) {
      case 'RU':
        return Core.utils.regional.ru.validateBankAccount(this.account, this.bank.id);
      case 'UZ':
        return Core.utils.regional.uz.validateBankAccount(this.account);
      default:
        return null;
    }
  }

  /** Валидация SWIFT. Пустое значение допустимо. */
  validateSwift(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const swift = value.toUpperCase();

    if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swift)) {
      return 'SWIFT должен быть в формате BIC (8 или 11 латинских символов)';
    }

    return null;
  }

  /**
   * Уже есть ли в выбранной компании счёт с таким же номером.
   * Проверка уникальности перед созданием.
   */
  isAccountDuplicate(): boolean {
    const existingAccounts = this.company?.bankAccounts ?? [];

    return existingAccounts.some(bankAccount => bankAccount.account === this.account);
  }

  static formatDiff(oldValue: BankAccount | null, newValue: BankAccount | null): string {
    if (!oldValue?.account || !newValue?.account || newValue.account === oldValue.account) {
      return '';
    }

    return `с ${oldValue.account.slice(-5)} ${oldValue.bank?.name} на ${newValue.account.slice(-5)} ${newValue.bank?.name}`;
  }
}

export default BankAccount;
